import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";

const AUTOSAVE_DELAY = 3000;

function getLocalKey(table, bookingId) {
  return `autosave_${table}_${bookingId}`;
}

export function useAutoSave({
  table,
  bookingId,
  form,
  buildPayload,
  buildInsertPayload,
  enabled = true
}) {
  const timerRef = useRef(null);
  const lastFingerprintRef = useRef("");
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastAutoSaved, setLastAutoSaved] = useState(null);
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const getFingerprint = useCallback((formData) => {
    return JSON.stringify(formData);
  }, []);

  const saveToLocal = useCallback((formData) => {
    if (!bookingId) return;
    try {
      const key = getLocalKey(table, bookingId);
      localStorage.setItem(key, JSON.stringify({
        form: formData,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.warn("LocalStorage save failed:", e);
    }
  }, [table, bookingId]);

  const loadFromLocal = useCallback(() => {
    if (!bookingId) return null;
    try {
      const key = getLocalKey(table, bookingId);
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const age = Date.now() - (parsed.timestamp || 0);
      if (age > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(key);
        return null;
      }
      return parsed.form;
    } catch (e) {
      return null;
    }
  }, [table, bookingId]);

  const clearLocal = useCallback(() => {
    if (!bookingId) return;
    try {
      localStorage.removeItem(getLocalKey(table, bookingId));
    } catch (e) {
      console.warn("LocalStorage clear failed:", e);
    }
  }, [table, bookingId]);

  const saveToDb = useCallback(async (formData) => {
    if (!bookingId || !enabled) return;

    const fingerprint = getFingerprint(formData);
    if (fingerprint === lastFingerprintRef.current) return;

    if (!mountedRef.current) return;
    setAutoSaving(true);

    try {
      const { data: existing } = await supabase
        .from(table)
        .select("id")
        .eq("booking_id", bookingId)
        .maybeSingle();

      const payload = buildPayload(formData);

      if (existing) {
        await supabase
          .from(table)
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq("id", existing.id);
      } else {
        const insertData = buildInsertPayload
          ? buildInsertPayload(formData)
          : { ...payload, booking_id: bookingId };
        await supabase
          .from(table)
          .insert(insertData);
      }

      lastFingerprintRef.current = fingerprint;
      if (mountedRef.current) {
        setLastAutoSaved(new Date());
      }
    } catch (err) {
      console.warn("Autosave to DB failed:", err);
    } finally {
      if (mountedRef.current) {
        setAutoSaving(false);
      }
    }
  }, [bookingId, enabled, table, buildPayload, buildInsertPayload, getFingerprint]);

  const scheduleAutoSave = useCallback((formData) => {
    if (!enabled || !bookingId) return;

    saveToLocal(formData);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      saveToDb(formData);
    }, AUTOSAVE_DELAY);
  }, [enabled, bookingId, saveToLocal, saveToDb]);

  const cancelAutoSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const markSaved = useCallback(() => {
    lastFingerprintRef.current = getFingerprint(form);
    clearLocal();
  }, [form, getFingerprint, clearLocal]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return {
    autoSaving,
    lastAutoSaved,
    restoredFromDraft,
    setRestoredFromDraft,
    scheduleAutoSave,
    cancelAutoSave,
    markSaved,
    loadFromLocal,
    clearLocal
  };
}
